import {
	createAddressDraft,
	formatAddressSummary,
	isAddressDraftEmpty,
	sanitizeAddressDraft,
	type AddressDraft,
} from '#app/routes/mocra+/_form+/components/addresses/types.ts'
import {
	FACILITY_TYPE_OPTIONS,
	FEI_STATUS_PER_FACILITY,
	REGISTRATION_STATUS_OPTIONS,
	type FacilityEntry,
} from '#app/routes/mocra+/_form+/components/facilities/schema.ts'

export type FacilityTypeValue = (typeof FACILITY_TYPE_OPTIONS)[number]['value']
export type FacilityRegistrationStatusValue =
	(typeof REGISTRATION_STATUS_OPTIONS)[number]['value']
export type FacilityFeiStatusValue =
	(typeof FEI_STATUS_PER_FACILITY)[number]['value']

export type FacilityDraft = {
	name: string
	type: FacilityTypeValue
	address: AddressDraft
	registrationStatus: FacilityRegistrationStatusValue
	feiStatus: FacilityFeiStatusValue
	feiNumber: string
}

export function createFacilityDraft(
	overrides: Partial<FacilityDraft> = {},
): FacilityDraft {
	return {
		name: '',
		type: FACILITY_TYPE_OPTIONS[0]?.value ?? 'owned',
		address: createAddressDraft(),
		registrationStatus: REGISTRATION_STATUS_OPTIONS[0]?.value ?? 'pending',
		feiStatus: FEI_STATUS_PER_FACILITY[0]?.value ?? 'not_applied',
		feiNumber: '',
		...overrides,
	}
}

export function sanitizeFacilityDraft(draft: FacilityDraft): FacilityDraft {
	const type = isFacilityTypeValue(draft.type)
		? draft.type
		: (FACILITY_TYPE_OPTIONS[0]?.value ?? 'owned')
	const registrationStatus = isFacilityRegistrationStatusValue(
		draft.registrationStatus,
	)
		? draft.registrationStatus
		: (REGISTRATION_STATUS_OPTIONS[0]?.value ?? 'pending')
	const feiStatus = isFacilityFeiStatusValue(draft.feiStatus)
		? draft.feiStatus
		: (FEI_STATUS_PER_FACILITY[0]?.value ?? 'not_applied')

	return {
		name: draft.name.trim(),
		type,
		address: sanitizeAddressDraft(draft.address),
		registrationStatus,
		feiStatus,
		feiNumber: feiStatus === 'present' ? draft.feiNumber.trim() : '',
	}
}

export function collectFacilityErrors(draft: FacilityDraft): string[] {
	const sanitized = sanitizeFacilityDraft(draft)
	const errors: string[] = []
	if (sanitized.name.length === 0) {
		errors.push('Enter the facility name.')
	}
	if (isAddressDraftEmpty(sanitized.address)) {
		errors.push('Add the facility address.')
	}
	return errors
}

export function formatFacilitySummary(draft: FacilityDraft): string {
	const sanitized = sanitizeFacilityDraft(draft)
	const fei =
		sanitized.feiNumber.length > 0 ? `FEI: ${sanitized.feiNumber}` : undefined
	const addressLines = formatAddressSummary(sanitized.address)
	return [fei, ...addressLines].filter(Boolean).join(' • ')
}

export function facilityCardTitle(
	draft: FacilityDraft,
	_index: number,
): string {
	const sanitized = sanitizeFacilityDraft(draft)
	const baseName = sanitized.name || 'Unnamed facility'
	return baseName
}

export function facilityFeiDescription(draft: FacilityDraft): string {
	const sanitized = sanitizeFacilityDraft(draft)
	return sanitized.feiNumber
		? `FEI: ${sanitized.feiNumber}`
		: 'FEI: Not provisioned'
}

export function facilityDraftToEntry(draft: FacilityDraft): FacilityEntry {
	const sanitized = sanitizeFacilityDraft(draft)
	return {
		name: sanitized.name,
		type: sanitized.type,
		address: sanitized.address,
		registrationStatus: sanitized.registrationStatus,
		feiStatus: sanitized.feiStatus,
		...(sanitized.feiNumber.length > 0
			? { feiNumber: sanitized.feiNumber }
			: {}),
	}
}

export function facilityEntryToDraft(entry: FacilityEntry): FacilityDraft {
	const type = isFacilityTypeValue(entry.type)
		? entry.type
		: (FACILITY_TYPE_OPTIONS[0]?.value ?? 'owned')
	const registrationStatus = isFacilityRegistrationStatusValue(
		entry.registrationStatus,
	)
		? entry.registrationStatus
		: (REGISTRATION_STATUS_OPTIONS[0]?.value ?? 'pending')
	const feiStatus = isFacilityFeiStatusValue(entry.feiStatus)
		? entry.feiStatus
		: (FEI_STATUS_PER_FACILITY[0]?.value ?? 'not_applied')
	return sanitizeFacilityDraft({
		name: entry.name,
		type,
		address: sanitizeAddressDraft(createAddressDraft(entry.address)),
		registrationStatus,
		feiStatus,
		feiNumber: entry.feiNumber ?? '',
	})
}

export function isFacilityTypeValue(
	value: unknown,
): value is FacilityTypeValue {
	return FACILITY_TYPE_OPTIONS.some((option) => option.value === value)
}

export function isFacilityRegistrationStatusValue(
	value: unknown,
): value is FacilityRegistrationStatusValue {
	return REGISTRATION_STATUS_OPTIONS.some((option) => option.value === value)
}

export function isFacilityFeiStatusValue(
	value: unknown,
): value is FacilityFeiStatusValue {
	return FEI_STATUS_PER_FACILITY.some((option) => option.value === value)
}
